// The admin app's shell: a top bar of five tabs (Organisations, Platform,
// Credentials, Operators, Spend), each a plain anchor to its path, with the
// operator's email and a sign-out on the right. See ADR-0039 and ADR-0051.

/** The five admin screens, named as the shell's tabs. */
export type AdminNav = "organisations" | "platform" | "credentials" | "operators" | "spend";

/** The admin tabs in display order, each with its label, its path and whether its page takes the full width. */
export const ADMIN_TABS: readonly { nav: AdminNav; label: string; path: string; full?: true }[] = [
  { nav: "organisations", label: "Organisations", path: "/" },
  { nav: "platform", label: "Platform", path: "/platform" },
  { nav: "credentials", label: "Credentials", path: "/credentials" },
  { nav: "operators", label: "Operators", path: "/operators" },
  { nav: "spend", label: "Spend", path: "/spend", full: true },
];

/** Which tab a path belongs to. The root is Organisations, and so is anything unknown. */
export function adminNavOf(pathname: string): AdminNav {
  const path = pathname.replace(/\/+$/, "") || "/";
  return ADMIN_TABS.find((t) => t.path !== "/" && (path === t.path || path.startsWith(t.path + "/")))?.nav ?? "organisations";
}

/** What the shell takes: the signed-in operator, the current tab, and the screen to render under it. */
export type AdminShellProps = {
  operator: { email: string };
  current: AdminNav;
  onSignOut: () => void;
  children?: React.ReactNode;
};

/**
 * The frame every admin screen renders inside: the tab bar, the page heading
 * for the current tab, and the children as the page body (a placeholder when
 * there are none). Rendered by the admin entry for every signed-in path.
 */
export function AdminShell({ operator, current, onSignOut, children }: AdminShellProps): JSX.Element {
  const tab = ADMIN_TABS.find((t) => t.nav === current)!;
  return (
    <>
      <header className="topbar admin-topbar">
        <a className="brand" href="/">
          <span className="logo" aria-hidden="true">
            T
          </span>
          TSV Factory · Platform
        </a>
        <nav className="nav" aria-label="Platform">
          {ADMIN_TABS.map((t) => (
            <a key={t.nav} href={t.path} aria-current={t.nav === current ? "page" : undefined}>
              {t.label}
            </a>
          ))}
        </nav>
        <div className="right">
          <span className="operator-email">{operator.email}</span>
          <button type="button" className="btn ghost sm" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>
      <main className={tab.full ? "page full" : "page"}>
        <div className="page-head">
          <h1>{tab.label}</h1>
        </div>
        {children ?? <p className="lede">Nothing here yet.</p>}
      </main>
    </>
  );
}
